import { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { useSEO } from '@/lib/useSEO';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatCOP, formatDate } from '@/lib/format';
import { toast } from 'sonner';
import { Check, Sparkles, ShieldCheck, Gift } from 'lucide-react';
import { startWompiPayment, getPaymentsConfig } from '@/lib/wompi';
import { PLAN_LABEL, COMMERCIAL_MSG } from '@/components/SellerModalityCard';

const FEATURES = {
  none: ['Modalidad base para vender', 'Panel de ventas y despachos', 'Carga masiva por CSV'],
  basic: ['Comisión reducida vs. Sin plan', 'Badge "Verificado" en tu tienda', 'Todas las funcionalidades estándar'],
  pro: ['Comisión mínima de la plataforma', 'Productos destacados primero en el catálogo', 'Badge PRO visible en tienda y productos', 'Soporte prioritario'],
};

const planTerms = (settings, plan) => ({
  price: settings[`${plan}_plan_price`],
  months: settings[`${plan}_plan_period_months`],
  commission: plan === 'none' ? settings.commission_pct : settings[`${plan}_plan_commission_pct`],
  limit: settings[`${plan}_plan_product_limit`],
});

export default function SellerSubscription() {
  const { user, sellerProfile, refresh } = useAuth();
  const [settings, setSettings] = useState(null);
  const [payConfig, setPayConfig] = useState({ mode: 'simulated', wompi: { enabled: false } });
  const [loading, setLoading] = useState(false);

  useSEO({
    title: 'Modalidad y plan | Panel del vendedor',
    description: 'Elige tu modalidad Sin plan, Básico o PRO para vender en AutoMarket Colombia.',
    canonical: '/vendedor/suscripcion',
  });

  useEffect(() => {
    api.get('/settings/public').catch(() => {}).then((r) => {
      if (r?.data) setSettings(r.data);
      else toast.error('No se pudo cargar la configuración de planes');
    });
    getPaymentsConfig().then(setPayConfig);
  }, []);

  const commercial = sellerProfile?.commercial;
  const benefitActive = !!commercial?.benefit?.active;
  const permanent = !!commercial?.permanent_pro;
  const approved = sellerProfile?.status === 'approved';
  const current = commercial?.plan?.active ? commercial.plan.plan : null;
  const wompiEnabled = !!payConfig?.wompi?.enabled;

  const select = async (plan) => {
    if (current && current !== plan && !benefitActive && !window.confirm(
      `Cambiar a ${PLAN_LABEL[plan]} reemplaza tu modalidad ${PLAN_LABEL[current]} desde hoy: pierdes el tiempo restante y aplican el precio, la comisión y el límite de productos de ${PLAN_LABEL[plan]}. ¿Continuar?`,
    )) return;
    setLoading(true);
    try {
      const method = wompiEnabled ? 'wompi' : 'simulated';
      const { data } = await api.post('/sellers/pay-plan', { plan, payment_method: method });
      if (data.pending_wompi) {
        await startWompiPayment({
          payment_id: data.payment.payment_id,
          customer_email: user?.email, customer_name: user?.name, customer_phone: user?.phone,
        });
        return;
      }
      toast.success(`Modalidad ${PLAN_LABEL[data.plan]} activa hasta ${formatDate(data.plan_expires_at)}`);
      await refresh();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Error');
    } finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Vendedor</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Modalidad y plan</h1>

        <StatusBanner commercial={commercial} permanent={permanent} />
        {benefitActive && <BenefitBanner benefit={commercial.benefit} />}

        <div className="mt-3 text-xs text-slate-500 flex items-center gap-2" data-testid="subscription-payment-mode">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
          {wompiEnabled
            ? <>Pagos seguros con <strong>Wompi</strong> ({payConfig?.wompi?.env === 'production' ? 'producción' : 'sandbox'})</>
            : <>Modo <strong>simulado</strong> (Preview).</>}
        </div>

        {settings && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {['none', 'basic', 'pro'].map((plan) => (
              <PlanCard
                key={plan}
                plan={plan}
                terms={planTerms(settings, plan)}
                current={current === plan}
                benefit={benefitActive ? commercial.benefit : null}
                ctaLabel={permanent ? null : (current === plan ? 'Renovar' : 'Elegir')}
                onSelect={() => select(plan)}
                disabled={loading || !approved}
              />
            ))}
          </div>
        )}
        <p className="mt-4 text-xs text-slate-500 border-l-2 border-slate-300 pl-3">
          Toda modalidad tiene vigencia. Al renovar la misma modalidad antes del vencimiento, la nueva vigencia se suma desde la fecha actual de vencimiento.
          Al vencer, no podrás vender hasta elegir o renovar una modalidad. Los pedidos anteriores conservan la comisión con la que se realizaron.
        </p>
      </div>
    </Layout>
  );
}

const StatusBanner = ({ commercial, permanent }) => {
  if (!commercial) return null;
  if (permanent) {
    return (
      <div className="mt-6 border-2 border-emerald-400 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="subscription-plan-permanent">
        PRO permanente sin costo (excepción otorgada por administración): sin vencimiento ni pagos de renovación.
      </div>
    );
  }
  const ok = commercial.can_sell;
  return (
    <div className={`mt-6 border-2 p-4 text-sm ${ok ? 'border-emerald-400 bg-emerald-50 text-emerald-900' : 'border-red-500 bg-red-50 text-red-800'}`} data-testid="subscription-commercial-status">
      {ok
        ? <>Modalidad <strong>{PLAN_LABEL[commercial.plan.plan]}</strong> vigente hasta el {formatDate(commercial.plan.expires_at)}</>
        : <strong>{COMMERCIAL_MSG[commercial.reason]}</strong>}
    </div>
  );
};

const BenefitBanner = ({ benefit }) => (
  <div className="mt-4 border-2 border-orange-500 bg-orange-50 p-4 text-sm text-slate-800 flex items-start gap-3" data-testid="subscription-benefit-banner">
    <Gift className="h-5 w-5 text-orange-600 shrink-0" />
    <div>
      <strong>Beneficio de lanzamiento (cupo #{benefit.slot})</strong> hasta el {formatDate(benefit.expires_at)}:
      eliges cualquier modalidad sin pagar el plan y la comisión por venta es del {benefit.commission_pct}%.
      Al terminar deberás elegir y pagar una modalidad para seguir vendiendo.
    </div>
  </div>
);

const PlanCard = ({ plan, terms, current, benefit, ctaLabel, onSelect, disabled }) => {
  const pro = plan === 'pro';
  return (
    <div className={`relative border-2 p-5 flex flex-col ${current ? (pro ? 'border-orange-500 bg-orange-50' : 'border-slate-900 bg-slate-50') : 'border-slate-200 bg-white'}`} data-testid={`plan-card-${plan}`}>
      {pro && (
        <Badge className="absolute -top-3 right-4 rounded-none bg-orange-500 text-slate-900 font-bold tracking-wider">
          <Sparkles className="h-3 w-3 mr-1" /> PREMIUM
        </Badge>
      )}
      {current && (
        <Badge className="absolute -top-3 left-4 rounded-none bg-emerald-600 text-white font-bold tracking-wider">
          <ShieldCheck className="h-3 w-3 mr-1" /> ACTUAL
        </Badge>
      )}
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Modalidad</div>
      <div className="font-display text-3xl font-black text-slate-900 mt-1">{PLAN_LABEL[plan]}</div>
      <div className="mt-3 flex items-baseline gap-2" data-testid={`plan-price-${plan}`}>
        <span className={`font-display text-2xl font-black ${benefit ? 'text-slate-400 line-through' : 'text-slate-900'}`}>{formatCOP(terms.price)}</span>
        {benefit && <span className="font-display text-xl font-black text-orange-700">Gratis</span>}
        <span className="text-xs text-slate-500">· {benefit ? 'durante el beneficio' : `cada ${terms.months} ${terms.months === 1 ? 'mes' : 'meses'}`}</span>
      </div>
      <div className="mt-1 text-sm" data-testid={`plan-commission-${plan}`}>
        Comisión por venta: <strong>{terms.commission}%</strong>
      </div>
      {benefit && (
        <div className="mt-1 text-xs font-semibold text-orange-700" data-testid={`plan-benefit-commission-${plan}`}>
          Beneficio de lanzamiento: {benefit.commission_pct}% hasta el {formatDate(benefit.expires_at)}; luego {terms.commission}%.
        </div>
      )}
      <div className="text-sm text-slate-600">Productos: {terms.limit ? `hasta ${terms.limit}` : 'ilimitados'}</div>
      <ul className="mt-4 space-y-2 text-sm flex-1">
        {FEATURES[plan].map((f) => (
          <li key={f} className="flex items-start gap-2 text-slate-700">
            <Check className={`h-4 w-4 mt-0.5 shrink-0 ${pro ? 'text-orange-600' : 'text-emerald-600'}`} />{f}
          </li>
        ))}
      </ul>
      {ctaLabel && (
        <Button
          data-testid={`plan-select-${plan}`}
          onClick={onSelect}
          disabled={disabled}
          className={`mt-4 rounded-none h-11 font-bold ${pro ? 'bg-orange-500 hover:bg-orange-400 text-slate-900' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}
        >
          {ctaLabel} {PLAN_LABEL[plan]}
        </Button>
      )}
    </div>
  );
};
